<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {useNavigationStore} from '@/stores/navigation-store'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Container from '@/components/containers/Container.vue'
import UserAvatar from '@/components/global/header/UserAvatar.vue'
import SearchDropdown from '@/components/global/header/SearchDropdown.vue'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {RouterLink, useRouter} from 'vue-router'
import {useAuthStore} from '@/stores/auth-store'
import defaultAvatar from '@/assets/images/user_avatar.png'
import {getUserId} from '@/constants'
import type {User} from '@/types/user-types'
import {getUser} from '@/api/users'
import type {GlobalSearchResponse} from "@/types/search";
import {searchApi} from "@/api/search";
// @ts-ignore
import {debounce} from "lodash-es";

const userId = getUserId()

const navStore = useNavigationStore()
const showDropdown = ref(false)
const dropdownRef = ref<HTMLDivElement | null>(null)
const userAvatarTriggerRef = ref<HTMLDivElement | null>(null)
const user = ref<User | null>(null)

const searchTerm = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)
const showSearchDropdown = ref(false)
const searchDropdownRef = ref<HTMLDivElement | null>(null)
const isSearchLoading = ref(false)
const searchResults = ref<GlobalSearchResponse | null>(null)
const searchError = ref<string | null>(null)
const searchContainerRef = ref<HTMLDivElement | null>(null)


const authStore = useAuthStore()
const router = useRouter()

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node
  if (
      dropdownRef.value &&
      !dropdownRef.value.contains(target) &&
      userAvatarTriggerRef.value &&
      !userAvatarTriggerRef.value.contains(target)
  ) {
    showDropdown.value = false
  }

  if (
      searchDropdownRef.value &&
      !searchDropdownRef.value.contains(target) &&
      searchContainerRef.value &&
      !searchContainerRef.value.contains(target)
  ) {
    showSearchDropdown.value = false
  }
}


const debouncedSearch = debounce(async (term: string) => {
  if (!term || term.length < 2) {
    searchResults.value = null
    showSearchDropdown.value = false
    return
  }

  isSearchLoading.value = true
  searchError.value = null
  showSearchDropdown.value = true

  try {
    searchResults.value = await searchApi.globalSearch(term, 10)
  } catch (error) {
    console.error('Search error:', error)
    searchError.value = 'Search failed. Please try again.'
    searchResults.value = null
  } finally {
    isSearchLoading.value = false
  }
}, 300)

watch(searchTerm, (newTerm) => {
  if (newTerm.trim().length === 0) {
    showSearchDropdown.value = false
    searchResults.value = null
    return
  }
  debouncedSearch(newTerm.trim())
})

const handleSearchFocus = () => {
  if (searchTerm.value.trim().length >= 2) {
    showSearchDropdown.value = true
  }
}

const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showSearchDropdown.value = false
    searchInputRef.value?.blur()
  }
}

const closeSearchDropdown = () => {
  showSearchDropdown.value = false
}

onMounted(async () => {
  document.addEventListener('mousedown', handleClickOutside)
  user.value = await getUser(userId)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

async function handleLogout() {
  showDropdown.value = false
  authStore.logout()
  await router.push('/login')
}

const userFullName = computed(() => {
  if (!user.value) return 'Loading...'
  return `${user.value.firstName} ${user.value.lastName}`
})
</script>

<template>
  <header class="bg-white h-[90px] flex items-center relative z-10">
    <Container class="w-full flex justify-between items-center">
      <div class="flex items-center gap-x-5">
        <Button text severity="secondary" @click="navStore.toggleSidebar">
          <Icon icon="iconamoon:menu-burger-horizontal-light" class="text-2xl" />
        </Button>

        <div class="relative" ref="searchContainerRef">
          <Icon
              icon="majesticons:search"
              class="absolute top-2/4 -mt-3 left-3 text-neutral-500 text-[22px] z-10"
          />
          <InputText
              ref="searchInputRef"
              v-model="searchTerm"
              placeholder="Search entire system"
              class="pl-10 py-4 w-[450px] text-sm"
              @focus="handleSearchFocus"
              @keydown="handleSearchKeydown"
          />
          <Icon
              icon="material-symbols:help"
              class="absolute top-2/4 -mt-3 right-3 text-neutral-500 text-[26px] z-10"
          />

          <SearchDropdown
              :is-visible="showSearchDropdown"
              :is-loading="isSearchLoading"
              :search-results="searchResults"
              :search-term="searchTerm"
              :error="searchError"
              @close="closeSearchDropdown"
          />
        </div>
      </div>

      <div class="relative">
        <div
          ref="userAvatarTriggerRef"
          @click="showDropdown = !showDropdown"
          class="cursor-pointer"
        >
          <UserAvatar
            :image="defaultAvatar"
            :name="`${userFullName}`"
          />
        </div>

        <div
          v-if="showDropdown"
          ref="dropdownRef"
          class="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl py-2 shadow-xl border border-gray-200 z-50"
        >
          <div class="px-4 py-3 border-b border-gray-200">
            <p class="text-sm font-semibold text-gray-700 truncate">
              {{ userFullName }}
            </p>
            <p class="text-xs text-gray-500 truncate">{{ user?.email }}</p>
          </div>

          <nav class="py-1">
            <RouterLink
              :to="{ name: 'UserProfile' }"
              custom
              v-slot="{ href, navigate, isActive, }"
            >
              <a
                :href="href"
                @click="navigate(); showDropdown = false;"
                class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 rounded-md mx-1"
                :class="[isActive ? 'bg-gray-100 text-primary-600' : 'text-gray-700 hover:text-gray-900']"
              >
                <Icon icon="fa6-solid:user-shield" class="w-5 h-5" :class="[isActive ? 'text-primary-500' : 'text-gray-400']" />
                <span>User Profile</span>
              </a>
            </RouterLink>

            <RouterLink
              :to="{ name: 'BankDetail' }"
            custom
            v-slot="{ href, navigate, isActive }"
            >
            <a
              :href="href"
              @click="navigate(); showDropdown = false;"
              class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 rounded-md mx-1"
              :class="[isActive ? 'bg-gray-100 text-primary-600' : 'text-gray-700 hover:text-gray-900']"
            >
              <Icon icon="mdi:bank" class="w-5 h-5" :class="[isActive ? 'text-primary-500' : 'text-gray-400']" />
              <span>Bank Details</span>
            </a>
            </RouterLink>

            <div
              class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer rounded-md mx-1 mt-1 border-t border-gray-200 pt-2"
              @click="handleLogout"
            >
              <Icon icon="majesticons:logout" class="text-gray-400 w-5 h-5" />
              <span>Logout</span>
            </div>
          </nav>
        </div>
      </div>
    </Container>
  </header>
</template>

<style scoped>
</style>